import BaseApiService from './BaseApiService.js';

class BrandingService extends BaseApiService {
  
  // Fetch branding settings for logged-in organization
  async getBranding() {
    return this.makeRequest('/api/admin/college/branding', { method: 'GET' });
  }

  // Save branding settings
  async saveBranding(brandingData) {
    return this.makeRequest('/api/admin/college/branding', {
      method: 'POST',
      body: JSON.stringify(brandingData)
    });
  }

  // Upload branding asset (logo, seal, or signature)
  async uploadAsset(type, file) {
    const formData = new FormData();
    formData.append('file', file);

    const token = this.getToken();
    const headers = {
      'ngrok-skip-browser-warning': 'true',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${this.baseURL}/api/admin/college/branding/upload/${type}`;
    
    console.log(`Uploading asset to: ${url}`);
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${errorText || response.statusText}`);
    }

    return await response.json();
  }

  // Fetch document templates
  async getTemplates() {
    return this.makeRequest('/api/admin/college/branding/templates', { method: 'GET' });
  }

  // Save/Create a document template
  async saveTemplate(templateData) {
    return this.makeRequest('/api/admin/college/branding/templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    });
  }

  // Public card verification endpoint
  async verifyCard(token) {
    // This is public, so no auth required
    const url = `${this.baseURL}/api/verify/card/${token}`;
    console.log(`Calling verification URL: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorObj;
      try {
        errorObj = JSON.parse(errorText);
      } catch (e) {
        errorObj = { error: errorText };
      }
      throw new Error(errorObj.error || `Verification failed: HTTP ${response.status}`);
    }

    return await response.json();
  }

  // Log card reprint history
  async auditPrint(auditData) {
    return this.makeRequest('/api/admin/college/branding/print-history', {
      method: 'POST',
      body: JSON.stringify(auditData)
    });
  }

  // Get print history logs
  async getPrintHistory() {
    return this.makeRequest('/api/admin/college/branding/print-history', { method: 'GET' });
  }

  // Get all issued certificates
  async getIssuedCertificates() {
    return this.makeRequest('/api/admin/college/certificates/issued', { method: 'GET' });
  }

  // Issue a new certificate
  async issueCertificate(payload) {
    return this.makeRequest('/api/admin/college/certificates/issue', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // Log reprint for an issued certificate
  async reprintCertificate(id, payload = {}) {
    return this.makeRequest(`/api/admin/college/certificates/reprint/${id}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // Revoke an issued certificate with optional reason
  async revokeCertificate(id, payload = {}) {
    return this.makeRequest(`/api/admin/college/certificates/revoke/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  // Check student fee clearance eligibility
  async checkFeeClearance(studentId, academicYear = '', feeId = '') {
    const q = new URLSearchParams();
    q.append('studentId', studentId);
    if (academicYear) q.append('academicYear', academicYear);
    if (feeId) q.append('feeId', feeId);
    return this.makeRequest(`/api/admin/college/certificates/fee-clearance/check?${q.toString()}`, { method: 'GET' });
  }

  // Parent fee clearance check
  async getParentFeeClearance(academicYear = '') {
    const q = academicYear ? `?academicYear=${encodeURIComponent(academicYear)}` : '';
    return this.makeRequest(`/api/parent/certificates/fee-clearance${q}`, { method: 'GET' });
  }

  // Public certificate verification
  async verifyCertificate(certificateNumber) {
    const url = `${this.baseURL}/api/verify/certificate/${certificateNumber}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'ngrok-skip-browser-warning': 'true'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorObj;
      try {
        errorObj = JSON.parse(errorText);
      } catch (e) {
        errorObj = { error: errorText };
      }
      throw new Error(errorObj.error || `Verification failed: HTTP ${response.status}`);
    }

    return await response.json();
  }
}

const brandingService = new BrandingService();
export default brandingService;
